import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  CheckCircle,
  Clock,
  Building,
  RefreshCw,
  Search,
  Eye,
  Loader2,
  XCircle,
  Info,
  Download
} from 'lucide-react';
import { transcriptionService } from '../services/transcriptionService';

const TranscriptionDashboard = () => {
  const [transcriptions, setTranscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    company: '',
    status: '',
    quarter: '',
    page: 1,
    limit: 10
  });
  const [companySearchInput, setCompanySearchInput] = useState('');
  const [transcriptModal, setTranscriptModal] = useState({ show: false, data: null, loading: false });
  const [downloadingRecordings, setDownloadingRecordings] = useState(new Set());

  // Helper function to format duration
  const formatDuration = (durationInSeconds) => {
    if (!durationInSeconds || durationInSeconds === 0) {
      return '0m 0s';
    }

    const totalSeconds = Math.floor(durationInSeconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      // For calls over 1 hour, show hours and minutes
      return `${hours}h ${minutes}m`;
    } else {
      // For calls under 1 hour, show minutes and seconds
      return `${minutes}m ${seconds}s`;
    }
  };

  // Helper function to get progress percentage (now handled by backend)
  const getProgress = (transcription) => {
    // Backend now calculates progress based on call duration and 2-minute chunks
    return transcription.progress_percentage || 0;
  };

  // Load dashboard data
  const loadDashboard = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);

      const params = {};
      if (filters.company) params.company_filter = filters.company;
      if (filters.status) params.status_filter = filters.status;
      if (filters.quarter) params.quarter_filter = filters.quarter;
      params.page = filters.page;
      params.limit = filters.limit;

      const [dashboardData, statsData] = await Promise.all([
        transcriptionService.getDashboardTranscriptions(params),
        transcriptionService.getDashboardStats()
      ]);

      setTranscriptions(dashboardData.items || []);
      setPagination(dashboardData.pagination || {});
      setStats(statsData);

    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [filters]);

  // Load active jobs for polling
  const loadActiveJobs = useCallback(async () => {
    try {
      const activeData = await transcriptionService.getActiveJobs();
      // Update active job statuses in the current transcriptions list
      if (activeData.active_jobs && activeData.active_jobs.length > 0) {
        setTranscriptions(prev => prev.map(trans => {
          const activeJob = activeData.active_jobs.find(job => job.job_id === trans.job_id);
          return activeJob ? { ...trans, ...activeJob } : trans;
        }));
      }
    } catch (err) {
      // Silently handle polling errors
      console.warn('Failed to load active jobs:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Polling for active jobs
  useEffect(() => {
    const interval = setInterval(loadActiveJobs, 30000); // Poll every 30 seconds (reduced from 5s)
    return () => clearInterval(interval);
  }, [loadActiveJobs]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  // Handle company search on Enter key
  const handleCompanySearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleFilterChange('company', companySearchInput);
    }
  };

  // Handle company search input change
  const handleCompanyInputChange = (value) => {
    setCompanySearchInput(value);
    // Clear the filter if input is empty
    if (value === '') {
      handleFilterChange('company', '');
    }
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { class: 'status-badge success', icon: CheckCircle },
      processing: { class: 'status-badge warning', icon: Clock },
      queued: { class: 'status-badge info', icon: Clock },
      failed: { class: 'status-badge error', icon: XCircle }
    };

    const config = statusConfig[status] || statusConfig.queued;
    const IconComponent = config.icon;

    return (
      <span className={config.class}>
        <IconComponent style={{ width: '12px', height: '12px' }} />
        {status}
      </span>
    );
  };

  // Handle view transcription
  const handleViewTranscription = async (jobId, status) => {
    try {
      setTranscriptModal({ show: true, data: null, loading: true });
      
      let response;
      
      // Use different endpoints based on transcription status
      if (status === 'completed') {
        // For completed transcriptions, fetch from database
        try {
          // Use the configured API instance instead of native fetch
          const apiResponse = await transcriptionService.getTranscriptionChunksFromDB(jobId);
          response = apiResponse;
        } catch (dbError) {
          console.warn('Failed to fetch from database, falling back to real-time endpoint:', dbError);
          // Fallback to real-time endpoint if DB fetch fails
          response = await transcriptionService.getTranscriptionChunks(jobId);
        }
      } else {
        // For processing/queued transcriptions, use real-time endpoint
        response = await transcriptionService.getTranscriptionChunks(jobId);
      }
      
      setTranscriptModal({ 
        show: true, 
        data: response, 
        loading: false 
      });
    } catch (error) {
      console.error('Error fetching transcript:', error);
      setTranscriptModal({ 
        show: true, 
        data: { error: error.message || 'Failed to load transcript' }, 
        loading: false 
      });
    }
  };

  // Close transcript modal
  const closeTranscriptModal = () => {
    setTranscriptModal({ show: false, data: null, loading: false });
  };

  // Handle recording download
  const handleDownloadRecording = async (callId, transcription) => {
    if (!callId || callId === 'N/A') {
      alert('No call ID available for this transcription');
      return;
    }

    try {
      setDownloadingRecordings(prev => new Set([...prev, callId]));
      
      // First check if recording is available
      const availability = await transcriptionService.checkRecordingAvailability(callId);
      
      if (!availability.available) {
        alert(`Recording not available for call ID: ${callId}\n${availability.error || 'Recording may not exist or is not accessible'}`);
        return;
      }

      // Start download
      await transcriptionService.downloadRecording(callId);
      
      // Show success message with file size if available
      const fileSizeMsg = availability.file_size_mb ? ` (${availability.file_size_mb} MB)` : '';
      alert(`Download started for ${transcription.company_name} recording${fileSizeMsg}`);
      
    } catch (error) {
      console.error('Error downloading recording:', error);
      alert(`Failed to download recording: ${error.message || 'Unknown error'}`);
    } finally {
      setDownloadingRecordings(prev => {
        const newSet = new Set(prev);
        newSet.delete(callId);
        return newSet;
      });
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="loading-container">
          <Loader2 style={{ width: '48px', height: '48px', animation: 'spin 1s linear infinite', color: '#3b82f6', marginBottom: '16px' }} />
          <p>Loading transcription dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <div className="container" style={{ paddingTop: '32px', paddingBottom: '32px' }}>
        {/* Header */}
        <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="dashboard-title">Transcription Dashboard</h1>
            <p className="dashboard-subtitle">Monitor transcription jobs, view progress, and manage results</p>
          </div>
          <button
            onClick={() => loadDashboard()}
            disabled={loading}
            className="btn btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <RefreshCw style={{ width: '16px', height: '16px' }} />
            Refresh
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-container">
            <div className="error-title">Error</div>
            <div className="error-message">{error}</div>
          </div>
        )}

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-4" style={{ marginBottom: '32px' }}>
            <div className="stats-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: '#3b82f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FileText style={{ width: '24px', height: '24px', color: 'white' }} />
                </div>
                <div>
                  <div className="stats-label">Total Transcriptions</div>
                  <div className="stats-number">
                    {stats.transcriptions?.total || 0}
                  </div>
                </div>
              </div>
            </div>

            <div className="stats-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircle style={{ width: '24px', height: '24px', color: 'white' }} />
                </div>
                <div>
                  <div className="stats-label">Completed</div>
                  <div className="stats-number">
                    {stats.transcriptions?.completed || 0}
                  </div>
                </div>
              </div>
            </div>

            <div className="stats-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: '#f59e0b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Clock style={{ width: '24px', height: '24px', color: 'white' }} />
                </div>
                <div>
                  <div className="stats-label">Processing</div>
                  <div className="stats-number">
                    {stats.transcriptions?.processing || 0}
                  </div>
                </div>
              </div>
            </div>

            <div className="stats-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: '#8b5cf6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Building style={{ width: '24px', height: '24px', color: 'white' }} />
                </div>
                <div>
                  <div className="stats-label">Companies</div>
                  <div className="stats-number">
                    {stats.companies_count || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="form-section" style={{ marginBottom: '32px' }}>
          <h2 className="form-title">Filters & Search</h2>
          <div className="filter-grid">
            <div className="form-group">
              <label className="label">
                <Search style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                Company Name
              </label>
              <input
                type="text"
                value={companySearchInput}
                onChange={(e) => handleCompanyInputChange(e.target.value)}
                onKeyPress={handleCompanySearchKeyPress}
                placeholder="Search by company name... (Press Enter to search)"
                className="input"
              />
            </div>

            <div className="form-group">
              <label className="label">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="select"
              >
                <option value="">All Status</option>
                <option value="completed">Completed</option>
                <option value="processing">Processing</option>
                <option value="queued">Queued</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div className="form-group">
              <label className="label">Quarter</label>
              <select
                value={filters.quarter}
                onChange={(e) => handleFilterChange('quarter', e.target.value)}
                className="select"
              >
                <option value="">All Quarters</option>
                <option value="Q1">Q1</option>
                <option value="Q2">Q2</option>
                <option value="Q3">Q3</option>
                <option value="Q4">Q4</option>
              </select>
            </div>
          </div>
        </div>

        {/* Transcriptions Table */}
        <div className="table-container">
          <div className="table-header">
            <h3 className="table-title">Transcription Jobs</h3>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              {pagination.total_items || 0} total transcriptions
            </div>
          </div>

          {transcriptions.length === 0 ? (
            <div style={{
              padding: '48px',
              textAlign: 'center',
              color: '#64748b',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px'
            }}>
              <FileText style={{ width: '48px', height: '48px', color: '#cbd5e1' }} />
              <div>
                <div style={{ fontWeight: '500', marginBottom: '4px' }}>No transcriptions found</div>
                <div style={{ fontSize: '14px' }}>Try adjusting your filters or refresh the page.</div>
              </div>
            </div>
          ) : (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Call ID</th>
                    <th>Call Type</th>
                    <th>Quarter/Date</th>
                    <th>Status</th>
                    <th>Progress</th>
                    <th>Duration</th>
                    <th>Created</th>
                    <th>Actions</th>
                    <th>Recording</th>
                  </tr>
                </thead>
                <tbody>
                  {transcriptions.map((transcription) => (
                    <tr key={transcription.job_id}>
                      <td>
                        <div>
                          <div style={{ fontWeight: '500', color: '#1e293b' }}>
                            {transcription.company_name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            {transcription.job_id}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '14px', fontFamily: 'monospace', color: '#1e293b' }}>
                          {transcription.call_id || 'N/A'}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '14px', color: '#1e293b' }}>
                          {transcription.call_type || 'N/A'}
                        </div>
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: '500' }}>{transcription.quarter}</div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            {transcription.date}
                          </div>
                        </div>
                      </td>
                      <td>
                        {getStatusBadge(transcription.status)}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            width: '60px',
                            height: '6px',
                            background: '#e2e8f0',
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${getProgress(transcription)}%`,
                              height: '100%',
                              background: transcription.status === 'completed' ? '#10b981' : '#3b82f6',
                              transition: 'width 0.3s ease'
                            }}></div>
                          </div>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>
                            {getProgress(transcription)}%
                          </span>
                        </div>
                        {transcription.total_chunks && (
                          <div style={{ fontSize: '11px', color: '#64748b' }}>
                            {transcription.processed_chunks || 0}/{transcription.total_chunks} chunks
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontSize: '14px' }}>
                          {formatDuration(transcription.processing_duration)}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '14px' }}>
                          {formatDate(transcription.created_at)}
                        </div>
                      </td>
                      <td>
                        <button
                          onClick={() => handleViewTranscription(transcription.job_id, transcription.status)}
                          className="btn btn-secondary"
                          style={{ 
                            padding: '6px 12px', 
                            fontSize: '12px',
                            minWidth: 'auto'
                          }}
                          title="View Full Transcript"
                        >
                          <Eye style={{ width: '14px', height: '14px' }} />
                          View
                        </button>
                      </td>
                      <td>
                        {transcription.call_id && transcription.call_id !== 'N/A' ? (
                          <button
                            onClick={() => handleDownloadRecording(transcription.call_id, transcription)}
                            disabled={downloadingRecordings.has(transcription.call_id)}
                            className="btn btn-primary"
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '12px',
                              minWidth: 'auto',
                              opacity: downloadingRecordings.has(transcription.call_id) ? 0.6 : 1
                            }}
                            title="Download Call Recording"
                          >
                            {downloadingRecordings.has(transcription.call_id) ? (
                              <Loader2 style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }} />
                            ) : (
                              <Download style={{ width: '14px', height: '14px' }} />
                            )}
                            {downloadingRecordings.has(transcription.call_id) ? 'Downloading...' : 'Download'}
                          </button>
                        ) : (
                          <span style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>
                            No recording
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div style={{
                  padding: '16px 24px',
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '14px', color: '#64748b' }}>
                    Showing {((pagination.current_page || 1) - 1) * (pagination.items_per_page || 10) + 1} to{' '}
                    {Math.min((pagination.current_page || 1) * (pagination.items_per_page || 10), pagination.total_items || 0)} of{' '}
                    {pagination.total_items || 0} results
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handlePageChange(pagination.current_page - 1)}
                      disabled={pagination.current_page <= 1}
                      className="btn btn-secondary"
                      style={{ fontSize: '14px', padding: '8px 12px' }}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.current_page + 1)}
                      disabled={pagination.current_page >= pagination.total_pages}
                      className="btn btn-secondary"
                      style={{ fontSize: '14px', padding: '8px 12px' }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Information Box */}
        <div className="info-box">
          <div className="info-title">
            <Info style={{ width: '16px', height: '16px' }} />
            Dashboard Information
          </div>
          <ul className="info-list">
            <li><strong>Real-time Updates:</strong> Dashboard automatically refreshes active job statuses every 5 seconds</li>
            <li><strong>View Transcripts:</strong> Click "View" to see the complete transcription results</li>
            <li><strong>Download Recordings:</strong> Click "Download" to get the original call recording (when available)</li>
            <li><strong>Company Search:</strong> Enter company name and press Enter to search</li>
            <li><strong>Filtering:</strong> Use filters to find specific transcriptions by company, status, or quarter</li>
            <li><strong>Progress Tracking:</strong> Monitor real-time progress of ongoing transcription jobs</li>
          </ul>
        </div>
      </div>

      {/* Transcript Modal */}
      {transcriptModal.show && (
        <div className="modal-overlay" onClick={closeTranscriptModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Full Transcript</h3>
              <button 
                onClick={closeTranscriptModal}
                className="modal-close-btn"
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {transcriptModal.loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Loader2 className="spin" style={{ width: '32px', height: '32px' }} />
                  <p>Loading transcript...</p>
                </div>
              ) : transcriptModal.data?.error ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
                  <p>Error: {transcriptModal.data.error}</p>
                </div>
              ) : (
                <div className="transcript-content">
                  {transcriptModal.data?.chunks?.map((chunk, index) => (
                    <div key={index} className="transcript-chunk" style={{
                      marginBottom: '20px',
                      padding: '15px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb'
                    }}>
                      <div style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        marginBottom: '8px',
                        fontWeight: '500'
                      }}>
                        Chunk {chunk.chunk_index} • {chunk.file_name} • Duration: {formatDuration(chunk.duration_seconds)}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: '#374151'
                      }}>
                        {chunk.full_transcript_text}
                      </div>
                    </div>
                  )) || (
                    <p style={{ textAlign: 'center', color: '#6b7280' }}>
                      No transcript chunks available
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscriptionDashboard;